import React, { useEffect, useState } from "react";
import { IoMdMail } from "react-icons/io";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaMap,
  FaPhone,
  FaTelegram,
  FaWhatsapp,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoClose } from "react-icons/io5";
import smartLogo from "../../assets/images/smart-logo.png";
import { Button, Container } from "./ui";
import { useSectionNav } from "./ui/useSectionNav";

const SOCIAL_LINKS = [
  { icon: FaWhatsapp, href: "https://wa.me/+919363282829", label: "WhatsApp" },
  {
    icon: FaFacebook,
    href: "https://www.facebook.com/profile.php?id=61590478562048",
    label: "Facebook",
  },
  {
    icon: FaInstagram,
    href: "https://www.instagram.com/smartica.in/",
    label: "Instagram",
  },
  { icon: FaXTwitter, href: "https://x.com/ExcelwithSMART", label: "Twitter" },
  {
    icon: FaLinkedin,
    href: "https://www.linkedin.com/company/101747817/admin/dashboard/",
    label: "LinkedIn",
  },
  {
    icon: FaTelegram,
    href: "https://t.me/excelwithsmarttraining",
    label: "Telegram",
  },
];

const Navbar = () => {
  const goToSection = useSectionNav();

  const [isStuck, setStuck] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  // Drives the shadow only. Deliberately does not change position or size:
  // reflowing the header mid-scroll would move the target of an in-flight
  // smooth scroll and make it lurch.
  useEffect(() => {
    const handleScroll = () => setStuck(window.scrollY > 5);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeSidebar = () => setSidebarOpen(false);

  const navLinks = [
    { label: "Home", onClick: () => goToSection("top") },
    { label: "Services", onClick: () => goToSection("services") },
    { label: "Skill Up With Us", onClick: () => goToSection("skillup") },
    { label: "Careers", href: "/careers" },
    { label: "Blogs", href: "/blogs" },
  ];

  const renderNavLink = (link, className) =>
    link.href ? (
      <a key={link.label} href={link.href} className={className}>
        {link.label}
      </a>
    ) : (
      <button key={link.label} onClick={link.onClick} className={className}>
        {link.label}
      </button>
    );

  return (
    // A fragment, not a wrapper: a sticky element only sticks within its parent's
    // box, so the nav has to sit directly in the page-level column to stay pinned
    // for the whole scroll.
    <>
      {/* Contact strip — scrolls out of view on its own */}
      <div className="border-b border-white/10 bg-[#1F2937] py-2.5 max-lg:hidden">
        <Container>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-white/70">
              <span className="flex items-center gap-2 text-xs font-medium">
                <FaMap className="h-3.5 w-3.5 text-brand-300" />
                Nungambakkam, Chennai — 600034
              </span>
              <span className="flex items-center gap-2 text-xs">
                <IoMdMail className="h-3.5 w-3.5 text-brand-300" />
                info@smartica.co.in
              </span>
              <span className="flex items-center gap-2 text-xs">
                <FaPhone className="h-3.5 w-3.5 text-brand-300" />
                +91 9500077606
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-white/70 transition-colors duration-300 hover:bg-brand hover:text-white"
                >
                  <social.icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* Main navigation — sticky keeps it in flow, so pinning it costs no layout shift.
          Shares the footer's dark shell so the page is bookended by the same surface. */}
      <nav
        className={`sticky top-0 z-40 bg-[#1F2937] transition-shadow duration-300 ${
          isStuck ? "shadow-lg" : ""
        }`}
      >
        <Container>
          <div className="flex h-20 items-center justify-between">
            <a href="/" className="flex-shrink-0">
              <img
                src={smartLogo}
                alt="SMART Logo"
                className="h-11 w-auto transition-transform duration-300 hover:scale-105 lg:h-14"
              />
            </a>

            {/* Centered links */}
            <div className="hidden items-center gap-8 lg:flex">
              {navLinks.map((link) =>
                renderNavLink(
                  link,
                  "text-sm font-semibold text-white/80 transition-colors duration-300 hover:text-white"
                )
              )}
            </div>

            {/* Primary action */}
            <div className="hidden lg:block">
              <Button onClick={() => goToSection("logins")}>
                Smartica Login
              </Button>
            </div>

            <button
              onClick={() => setSidebarOpen(true)}
              className="rounded-lg p-2 text-white transition-colors duration-300 hover:bg-white/10 lg:hidden"
              aria-label="Open main menu"
            >
              <GiHamburgerMenu className="h-6 w-6" />
            </button>
          </div>
        </Container>

        {/* Mobile menu — sits above the sticky nav */}
        <div
          className={`fixed inset-0 z-50 bg-ink/40 transition-opacity duration-300 ${
            isSidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          onClick={closeSidebar}
        />
        <div
          className={`fixed right-0 top-0 z-[60] h-full w-80 max-w-sm bg-shell transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between border-b border-white/10 p-6">
            <img src={smartLogo} alt="SMART Logo" className="h-10" />
            <button
              onClick={closeSidebar}
              className="rounded-lg p-2 text-white transition-colors duration-300 hover:bg-white/10"
              aria-label="Close menu"
            >
              <IoClose className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-8">
            <nav className="space-y-1">
              {navLinks.map((link) =>
                renderNavLink(
                  { ...link, onClick: link.onClick && (() => { link.onClick(); closeSidebar(); }) },
                  "block w-full rounded-xl px-4 py-3 text-left text-base font-semibold text-white/80 transition-colors duration-300 hover:bg-white/10 hover:text-white"
                )
              )}
            </nav>

            <Button
              className="mt-6 w-full"
              onClick={() => {
                closeSidebar();
                goToSection("logins");
              }}
            >
              Smartica Login
            </Button>

            <div className="mt-10">
              <p className="mb-4 text-xs font-bold uppercase tracking-wider text-white/50">
                Follow Us
              </p>
              <div className="flex flex-wrap gap-2">
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-300 hover:bg-brand"
                  >
                    <social.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
